import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Pause, Play } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useClock } from '@/hooks/use-clock'
import { introDelay } from '@/lib/intro'
import { motionStore, useMotionPaused } from '@/lib/motion'
import { SiteMenu } from '@/components/generals/site-menu'

import { NAV_ITEMS } from '@/constants/navigation'

interface SiteHeaderProps {
    overlay?: boolean
}

export const SiteHeader: FC<SiteHeaderProps> = ({ overlay = false }): ReactNode => {
    const router = useRouter()
    const clock = useClock('Europe/Stockholm')
    const motionPaused = useMotionPaused()
    const [menuOpen, setMenuOpen] = useState(false)
    const [hidden, setHidden] = useState(false)
    const [atTop, setAtTop] = useState(true)
    const menuButtonRef = useRef<HTMLButtonElement>(null)

    const closeMenu = useCallback((): void => {
        setMenuOpen(false)
        menuButtonRef.current?.focus()
    }, [])

    useEffect(() => {
        let last = window.scrollY
        let raf = 0

        const update = (): void => {
            const y = window.scrollY
            const delta = y - last

            if (Math.abs(delta) > 4) {
                setHidden(delta > 0 && y > 96)
                last = y
            }

            setAtTop(y < window.innerHeight * 0.85)
        }

        const onScroll = (): void => {
            cancelAnimationFrame(raf)
            raf = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => {
            cancelAnimationFrame(raf)
            window.removeEventListener('scroll', onScroll)
        }
    }, [])

    useEffect(() => {
        document.documentElement.setAttribute('data-header', hidden && !menuOpen ? 'hidden' : 'shown')
    }, [hidden, menuOpen])

    const isActive = (href: string): boolean => router.pathname === href || router.pathname.startsWith(`${href}/`)
    const transparent = overlay && atTop

    // On Home the opening draws the items in from left to right, 60 ms apart (inert elsewhere).
    const contactDelay = 120 + NAV_ITEMS.length * 60

    return (
        <>
            <header
                onFocusCapture={() => setHidden(false)}
                className={cn(
                    'site-header fixed inset-x-0 top-0 z-40 h-14 border-b border-hairline transition-[transform,background-color] duration-500 ease-instrument desk:h-16',
                    transparent ? 'bg-transparent' : 'bg-background',
                    hidden && !menuOpen && '-translate-y-full'
                )}
            >
                <span className='intro-hairline pointer-events-none absolute inset-x-0 -bottom-px h-px origin-left bg-hairline' aria-hidden='true' />

                <div className='relative flex h-full items-center gap-3 px-gutter'>
                    <button
                        ref={menuButtonRef}
                        type='button'
                        onClick={() => setMenuOpen(true)}
                        aria-label='Open menu'
                        aria-expanded={menuOpen}
                        aria-controls='site-menu'
                        className='intro-fade group -ml-2 flex size-10 items-center justify-center'
                        style={introDelay(0)}
                    >
                        <span className='flex w-5 flex-col gap-[5px]' aria-hidden='true'>
                            <span className='h-px w-full bg-ink' />
                            <span className='h-px w-full bg-ink' />
                            <span className='h-px w-3/5 bg-ink transition-[width] duration-500 ease-instrument group-hover:w-full' />
                        </span>
                    </button>

                    <Link
                        href='/'
                        aria-label='Norrvik, home'
                        className='intro-fade font-heading text-[15px] font-semibold tracking-[0.2em] text-ink'
                        style={introDelay(60)}
                    >
                        NORRVIK
                    </Link>

                    <nav aria-label='Primary' className='absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 lg:block'>
                        <ul className='flex items-center gap-6'>
                            {NAV_ITEMS.map((item, index) => {
                                const active = isActive(item.href)

                                return (
                                    <li key={item.href} className='intro-fade' style={introDelay(120 + index * 60)}>
                                        <Link
                                            href={item.href}
                                            aria-current={active ? 'page' : undefined}
                                            className={cn(
                                                'hud group inline-flex items-start gap-1 py-2 transition-colors duration-300',
                                                active ? 'text-ink' : 'text-mute hover:text-ink'
                                            )}
                                        >
                                            <span>
                                                {item.label}
                                            </span>

                                            <sup className={cn('relative -top-0.5 text-[9px] leading-none tracking-[0.08em]', active ? 'text-signal' : 'text-mute')}>
                                                {item.index}
                                            </sup>
                                        </Link>
                                    </li>
                                )
                            })}
                        </ul>
                    </nav>

                    <div className='ml-auto flex items-center gap-4'>
                        <span className='intro-fade hud flex items-center gap-2.5 text-ink' style={introDelay(contactDelay + 120)}>
                            <span className='signal-dot' aria-hidden='true' />

                            <span className='hidden xl:inline'>
                                Live model
                            </span>
                        </span>

                        <span className='intro-fade hud hidden text-mute 2xl:inline' style={introDelay(contactDelay + 160)} suppressHydrationWarning>
                            <span className='sr-only'>
                                Stockholm local time
                            </span>

                            <span aria-hidden='true'>
                                STO&nbsp;
                            </span>

                            <time className='text-ink' suppressHydrationWarning>
                                {clock.text}
                            </time>
                        </span>

                        <button
                            type='button'
                            onClick={() => motionStore.set(!motionPaused)}
                            aria-pressed={motionPaused}
                            aria-label={motionPaused ? 'Resume all motion' : 'Pause all motion'}
                            title={motionPaused ? 'Resume all motion' : 'Pause all motion'}
                            className={cn(
                                'intro-fade flex size-9 items-center justify-center border transition-colors duration-300',
                                motionPaused ? 'border-signal text-signal' : 'border-ink/40 text-ink hover:border-ink'
                            )}
                            style={introDelay(contactDelay + 40)}
                        >
                            {motionPaused
                                ? <Play className='size-4' strokeWidth={1.5} aria-hidden='true' />
                                : <Pause className='size-4' strokeWidth={1.5} aria-hidden='true' />}
                        </button>

                        <Link
                            href='/contact'
                            aria-current={isActive('/contact') ? 'page' : undefined}
                            className={cn(
                                'intro-fade hud inline-flex h-9 items-center border px-4 transition-colors duration-300',
                                isActive('/contact') ? 'border-signal text-ink' : 'border-ink/40 text-ink hover:border-ink'
                            )}
                            style={introDelay(contactDelay)}
                        >
                            Contact
                        </Link>
                    </div>
                </div>
            </header>

            <SiteMenu id='site-menu' open={menuOpen} onClose={closeMenu} />
        </>
    )
}
